import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from "react-i18next";
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  FaPlaneDeparture,
  FaHotel,
  FaMapMarkedAlt,
  FaUsers,
  FaArrowRight,
  FaCheckCircle,
  FaStar,
} from "react-icons/fa";
import { Sparkles } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { getServices } from "../../slice/serviceSlice";

import img1 from "../../Assets/img/adinath-temple-khajuraho.jpg";
import img2 from "../../Assets/img/shantinath-temple-khajuraho.jpg";
import img3 from "../../Assets/img/chitragupta-temple-khajuraho.jpg";
import img4 from "../../Assets/img/dulhadev-temple-khajuraho.webp";

const ServiceBlock = ({ step, index }) => {
  const { t } = useTranslation();
  const Icon = step.icon;
  const isEven = index % 2 === 0;

  return (
    <section className={`py-16 md:py-24 ${step.bg}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          
          {/* TEXT BLOCK */}
          <motion.div 
            initial={{ opacity: 0, x: isEven ? -50 : 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6 }}
            className={`flex flex-col gap-6 ${!isEven ? 'lg:order-2' : ''}`}
          >
            {/* <div className="flex items-center gap-3">
              <span className="block w-10 h-0.5 bg-orange-main" />
              <span className="text-xs font-bold uppercase tracking-widest text-orange-main">
                Service {step.number}
              </span>
            </div> */}

            <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-gray-900 leading-tight uppercase">
              {step.title}
            </h2>

            <div className="inline-flex items-center gap-2 self-start px-4 py-1.5 rounded-full border border-orange-main/30 bg-orange-main/5">
              <Icon size={12} className="text-orange-main" />
              <span className="text-xs font-bold uppercase tracking-wider text-orange-main">
                {t(step.tag)}
              </span>
            </div>

            <div className="flex flex-col gap-3 max-w-lg">
              {step.description.map((desc, i) => (
                <p key={i} className="text-base text-gray-600 leading-relaxed">
                  {desc}
                </p>
              ))}
            </div>

            <div className="flex items-center gap-6 mt-4">
              {/* <div className="flex flex-col items-center px-5 py-3 rounded-xl border border-orange-main/30 bg-orange-main/5 min-w-[100px]">
                <span className="text-2xl font-black text-orange-main leading-tight">
                  {step.stat.value}
                </span>
                <span className="text-[10px] font-bold uppercase tracking-wider text-orange-main mt-1">
                  {step.stat.label}
                </span>
              </div> */}

              <Link to="/contact">
                <motion.div
                  whileHover={{ x: 6 }}
                  className="flex items-center gap-2 text-sm font-bold text-gray-900 cursor-pointer"
                >
                  {t('services_book')}
                  <FaArrowRight size={14} className="text-orange-main" />
                </motion.div>
              </Link>
            </div>
          </motion.div>

          {/* IMAGE BLOCK */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6 }}
            className={`relative rounded-3xl overflow-hidden shadow-2xl h-[300px] sm:h-[400px] lg:h-[500px] ${!isEven ? 'lg:order-1' : ''}`}
          >
            <img
              src={step.image}
              alt={step.title}
              className="w-full h-full object-cover transition-transform duration-700 hover:scale-105"
            />
            
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />

            <div className="absolute bottom-6 left-6 right-6 flex justify-between items-end">
              {/* <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/90 backdrop-blur-md shadow-sm">
                <span className="text-[10px] font-black uppercase tracking-widest text-gray-800">
                  Step
                </span>
                <span className="text-lg font-black text-orange-main leading-none">
                  {step.number}
                </span>
              </div> */}

              
            </div>
          </motion.div>

        </div>
      </div>
    </section>
  );
};

const Services = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { data: dynamicServices, loading } = useSelector((state) => state.service || { data: [], loading: false });

  const assetBaseUrl = process.env.REACT_APP_BACKEND
    ? process.env.REACT_APP_BACKEND.replace('/khajuraho/', '/').replace(/\/$/, '') + '/'
    : '';

  useEffect(() => {
    window.scrollTo(0, 0);
    dispatch(getServices());
  }, [dispatch]);

  const mappedDynamicServices = (dynamicServices || []).map((srv, index) => {
    let parsedImages = [];
    if (Array.isArray(srv.images)) {
      parsedImages = srv.images;
    } else if (typeof srv.images === 'string' && srv.images.trim().length > 0) {
      try {
        const maybeArray = JSON.parse(srv.images);
        parsedImages = Array.isArray(maybeArray) ? maybeArray : [srv.images];
      } catch (e) {
        parsedImages = [srv.images];
      }
    }
    const firstImage = parsedImages.length > 0 ? parsedImages[0] : '';
    let image = img1;
    if (firstImage === 'frontend_hotel') image = img1;
    else if (firstImage === 'frontend_event') image = img2;
    else if (firstImage === 'frontend_tour') image = img3;
    else if (firstImage === 'frontend_flight') image = img4;
    else if (firstImage) {
      image = firstImage.startsWith('http') ? firstImage : `${assetBaseUrl}${String(firstImage).replace(/^\/+/, '')}`;
    }

    const plainContent = String(srv.content || srv.description || srv.desc || srv.details || srv.text || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();

    return {
      number: (`0${index + 1}`).slice(-2),
      title: srv.heading || srv.title || srv.name || 'Service',
      icon: FaStar,
      bg: index % 2 === 0 ? 'bg-white' : 'bg-[#fbf4e8]',
      image: image,
      tag: 'Premium Service',
      description: [plainContent],
      stat: { value: '', label: '' },
    };
  });

  const servicesData = mappedDynamicServices;

  return (
    <main className="w-full bg-white">
      <Helmet>
        <title>Services | Khajuraho Routes</title>
        <meta name="description" content="Explore our premium services including custom tour packages, luxury transport, experienced guides, and seamless flight ticketing for your Khajuraho journey." />
        <meta name="keywords" content="Khajuraho travel services, Khajuraho tour guides, luxury transport Khajuraho, flight ticketing, custom tours" />
        <link rel="canonical" href="https://khajurahoroutes.com/services" />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://khajurahoroutes.com/services" />
        <meta property="og:title" content="Services | Khajuraho Routes" />
        <meta property="og:description" content="Explore our premium services including custom tour packages, luxury transport, experienced guides, and seamless flight ticketing for your Khajuraho journey." />
        <meta property="og:image" content="https://khajurahoroutes.com/android-chrome-512x512.png" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Services | Khajuraho Routes" />
        <meta name="twitter:description" content="Explore our premium services including custom tour packages, luxury transport, experienced guides, and seamless flight ticketing for your Khajuraho journey." />
        <meta name="twitter:image" content="https://khajurahoroutes.com/android-chrome-512x512.png" />

        {/* JSON-LD Schema */}
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "Service",
              "name": "Khajuraho Routes Services",
              "provider": {
                "@type": "TravelAgency",
                "name": "Khajuraho Routes"
              },
              "areaServed": {
                "@type": "City",
                "name": "Khajuraho"
              },
              "description": "Custom tour packages, luxury transport, expert tour guides, and flight ticketing in Khajuraho."
            }
          `}
        </script>
      </Helmet>
      
      {/* ─── HERO ──────────────────────────────────────────── */}
      <section className="relative w-full h-[60vh] min-h-[500px] flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${img1})` }}
        />
        <div className="absolute inset-0 bg-black/60" />
        
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="relative z-10 text-center px-4 max-w-4xl mx-auto"
        >
          <div className="mb-6 inline-flex items-center gap-2 px-4 py-2 rounded-full border border-orange-main/50 bg-orange-main/20 backdrop-blur-md">
            <Sparkles size={14} className="text-orange-main" />
            <span className="text-xs font-bold uppercase tracking-widest text-orange-main">
              Khajuraho Routes
            </span>
          </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-black text-white leading-tight tracking-tight mb-6">
            {t('services_title')}
          </h1>

          <p className="text-base md:text-lg text-gray-300 font-medium max-w-2xl mx-auto mb-10 leading-relaxed">
            {t('services_desc')}
          </p>

          <div className="flex flex-wrap items-center justify-center gap-4">
            <Link
              to="/tours"
              className="bg-orange-main text-white px-8 py-3.5 rounded-xl font-bold text-sm shadow-lg shadow-orange-main/30 hover:bg-orange-600 transition-colors"
            >
              Explore Packages
            </Link>
            <Link
              to="/contact"
              className="bg-white/10 backdrop-blur-md text-white border border-white/20 px-8 py-3.5 rounded-xl font-bold text-sm hover:bg-white/20 transition-colors"
            >
              Contact Us
            </Link>
          </div>
        </motion.div>
      </section>

      {/* ─── ZIG ZAG SERVICES SECTION ──────────────────────── */}
      <div className="w-full">
        {servicesData.map((step, idx) => (
          <ServiceBlock key={idx} step={step} index={idx} />
        ))}
      </div>

    </main>
  );
};

export default Services;
